import React, { useState, useContext, useRef, useEffect } from 'react';
import {
	Input,
	FormControl,
	FormErrorMessage,
	Text,
	Flex,
	FormLabel,
	InputGroup,
	InputRightElement,
	Spinner,
	useToast,
} from 'src/components/ui';
import { CampaignContext } from 'src/contexts';
import AssistantIcon from 'src/components/common/AssistantIcon';
import { ICreative } from 'src/lib/schemas/campaign/newFlowCampaign';
import {
	generateCreativeContent,
	CreativeFieldType,
} from 'src/services/campaign-copilot';

interface ValidatedInputProps {
	label?: string;
	value: string;
	onChange: (value: string) => void;
	maxLength?: number;
	isTextarea?: boolean;
	withAssistant?: boolean;
	name: string;
	creativeData?: ICreative;
	displayName?: string;
	setFieldValue?: (field: string, value: string) => void;
}

// Map field names to CreativeFieldType
const mapFieldNameToType = (fieldName: string): CreativeFieldType => {
	const mapping: Record<string, CreativeFieldType> = {
		headline: 'headline',
		headlines: 'headline',
		primary_text: 'primary_text',
		primaryText: 'primary_text',
		description: 'description',
		descriptions: 'description',
		long_headline: 'long_headline',
		longHeadline: 'long_headline',
		business_name: 'business_name',
		businessName: 'business_name',
		call_to_action: 'call_to_action',
		callToAction: 'call_to_action',
	};
	return mapping[fieldName] || 'headline';
};

const InputField: React.FC<ValidatedInputProps> = ({
	label,
	value,
	onChange,
	maxLength,
	isTextarea = false,
	withAssistant = false,
	name,
	creativeData,
	displayName,
}) => {
	const [error, setError] = useState<string | null>(null);
	const [isGenerating, setIsGenerating] = useState(false);
	const { id: campaignId } = useContext(CampaignContext);
	const inputRef = useRef<HTMLDivElement | null>(null);
	const [isFocused, setIsFocused] = useState(false);
	const toast = useToast();

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const newValue = e.target.value;

		if (maxLength && newValue.length > maxLength) {
			setError(`Maximum length is ${maxLength} characters`);
		} else {
			setError(null);
		}

		onChange(newValue);

		if (isTextarea) {
			handleAutoResize(e.target as HTMLTextAreaElement);
		}
	};

	const handleAutoResize = (textareaElement: HTMLTextAreaElement) => {
		textareaElement.style.height = 'auto';
		textareaElement.style.height = `${textareaElement.scrollHeight}px`;
	};

	useEffect(() => {
		if (isTextarea && inputRef.current) {
			const textarea = inputRef.current.querySelector('textarea');
			if (textarea) handleAutoResize(textarea as HTMLTextAreaElement);
		}
	}, [isTextarea, inputRef, value]);

	const handleGenerateContent = async () => {
		if (!campaignId || isGenerating) return;

		setIsGenerating(true);
		try {
			const fieldType = mapFieldNameToType(name);
			const result = await generateCreativeContent({
				campaignId,
				fieldName: fieldType,
				maxChars: maxLength || 100,
				channel: creativeData?.channel,
				currentValue: value,
			});

			// Update the field with generated content
			onChange(result.content);

			toast({
				title: 'Content generated',
				description: `${result.charCount} characters`,
				status: 'success',
				duration: 2000,
				isClosable: true,
			});
		} catch (err) {
			console.error('Failed to generate content:', err);
			toast({
				title: 'Generation failed',
				description:
					err instanceof Error ? err.message : 'Failed to generate content',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		} finally {
			setIsGenerating(false);
		}
	};

	return (
		<FormControl isInvalid={!!error}>
			{label && <FormLabel>{label}</FormLabel>}
			<InputGroup ref={inputRef}>
				<Input
					value={value}
					onChange={handleInputChange}
					maxLength={maxLength}
					bg="white"
					as={isTextarea ? 'textarea' : 'input'}
					minHeight={isTextarea ? '80px' : ''}
					onClick={(e) => e.stopPropagation()}
					fontSize="sm"
					pr={withAssistant ? 10 : 0}
					pt={isTextarea ? 1 : 0}
					pb={isTextarea ? 1 : 0}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					style={{ resize: 'none', overflow: 'hidden' }}
				/>
				{withAssistant && (
					<InputRightElement
						width="10px"
						height="100%"
						display="flex"
						right={isTextarea ? '17px' : '15px'}
						alignItems="flex-end"
						justifyContent="center"
						pb={isTextarea ? 0 : 1}
						pr={isTextarea ? 0 : 3}
					>
						{isGenerating ? (
							<Spinner size="sm" color="orange.500" />
						) : (
							<AssistantIcon onOpen={handleGenerateContent} />
						)}
					</InputRightElement>
				)}
			</InputGroup>
			{error && <FormErrorMessage>{error}</FormErrorMessage>}
			{maxLength && (
				<Flex justifyContent="flex-end">
					<Text fontSize="sm" color="gray.500">
						{value.length}/{maxLength}
					</Text>
				</Flex>
			)}
		</FormControl>
	);
};

export default InputField;
